import {
  type BehaviorSubject,
  type Observable,
  type Observer,
  Subject,
} from 'rxjs'
import {
  startTransition,
  useCallback,
  useEffect,
  useEffectEvent,
  useState,
} from 'react'

/**
 * Subscribes to the observable and calls the callback with the given value.
 *
 * Make sure to wrap the source in `useMemo`/`useCallback` as needed.
 *
 * @param source The source to subscribe to.
 * @param observerOrNext The observer.
 */
export function useObservable<T>(
  source: Observable<T> | (() => Observable<T>) | undefined,
  observerOrNext?: (value: T) => void,
) {
  const fn = useEffectEvent((value: T) => observerOrNext?.(value))
  useEffect(() => subscribe(source, fn), [source])
}

/**
 * Subscribes to the observable and returns the current value as state.
 *
 * Make sure to wrap the source and observer in `useMemo`/`useCallback` as needed.
 *
 * @param source The observable to subscribe to.
 * @param options Options for how to handle state.
 */
export function useObservableValue<T>(
  source: Observable<T> | (() => Observable<T>),
  options?:
    { transition?: boolean } | { transition?: boolean; initial: T | (() => T) },
) {
  const wrapInStartTransition = Boolean(options?.transition)
  const [value, setValue] = useState(() => {
    // If the options specify an initial value, use that.
    if (options && 'initial' in options) {
      return typeof options.initial === 'function'
        ? (options.initial as () => T)()
        : options.initial
    }

    // Subscribe to grab the latest value, then unsubscribe.
    let emitted = false
    let initialValue: T
    const unsubscribe = subscribe(source, (value) => {
      emitted = true
      initialValue = value
    })
    unsubscribe()

    if (!emitted) {
      throw new Error(
        '[useObservableValue] observable did not emit a value synchronously, and no initial value provided',
      )
    }

    return initialValue!
  })

  useObservable(
    source,
    useCallback(
      (next: T) => {
        if (wrapInStartTransition) {
          startTransition(() => setValue(next))
        } else {
          setValue(next)
        }
      },
      [wrapInStartTransition],
    ),
  )

  return value
}

/**
 * Creates a subject for the component. The returned subject is stable
 * between renders. Keep in mind the subject never completes.
 *
 * @param factory
 */
export function useSubject<T>(
  factory: () => BehaviorSubject<T>,
): BehaviorSubject<T>
export function useSubject<T>(): Subject<T>
export function useSubject<T>(factory?: () => Subject<T>) {
  const [subject] = useState<Subject<T>>(() =>
    factory ? factory() : new Subject(),
  )
  return subject
}

/**
 * Subscribes to the observable returned by the function, or the
 * observable itself.
 *
 * @param source
 * @param observerOrNext
 */
function subscribe<T>(
  source: Observable<T> | (() => Observable<T>) | undefined,
  observerOrNext: Partial<Observer<T>> | ((value: T) => void) | undefined,
) {
  if (!source) {
    return noop
  }

  const source$ = typeof source === 'function' ? source() : source
  const sub = source$.subscribe(observerOrNext)
  return () => sub.unsubscribe()
}

/**
 * Placeholder
 */
function noop() {
  //
}
